import type { Persona } from "@peer-start/platform-contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ChevronsUpDown, Languages, LogOut, UserRound } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { fetchLoginStatus, signOut } from "../../domains/auth/api/client.js";
import { clearAllProductTourSessionDismissals } from "../../domains/passport/components/product-tour-storage.js";
import { loginQueryKey } from "../../domains/auth/components/LoginGate.js";
import { personaRoleKey } from "../../domains/prototype/data/user-management.js";
import { profilePhotos } from "../profile-photos.js";
import { fadeClass } from "./sidebar-state.js";

function demoEmail(personaId: string): string {
  return `${personaId}@peer-start.demo`;
}

function setLanguage(language: "nl" | "en") {
  localStorage.setItem("peer-start-language", language);
}

interface ProfileMenuProps {
  persona: Persona;
  personas: readonly Persona[];
  onPersonaChange: (id: string) => void;
  className?: string;
  expanded?: boolean;
  labelsVisible?: boolean;
  placement?: "sidebar" | "header";
  roleTitle?: string;
}

export function ProfileMenu({
  persona,
  personas,
  onPersonaChange,
  className = "",
  expanded = false,
  labelsVisible = true,
  placement = "sidebar",
  roleTitle,
}: ProfileMenuProps) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const menuId = useId();
  const switcherId = useId();
  const root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<"main" | "language">("main");
  const status = useQuery({ queryKey: loginQueryKey, queryFn: fetchLoginStatus });
  const logout = useMutation({
    mutationFn: signOut,
    onSuccess: ({ logoutUrl }) => {
      clearAllProductTourSessionDismissals();
      client.clear();
      window.location.assign(logoutUrl);
    },
  });
  const canSignOut = Boolean(status.data?.enabled && status.data.authenticated);
  const email = demoEmail(persona.id);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setPanel("main");
  }, [open]);

  const chooseLanguage = (language: "nl" | "en") => {
    void i18n.changeLanguage(language);
    setLanguage(language);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={root}>
      <button
        aria-controls={menuId}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t("profile.menuLabel", { name: persona.name })}
        className={`flex items-center gap-3 overflow-hidden rounded-xl text-left transition-colors hover:bg-sidebar-foreground/10 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-sidebar-foreground/60 ${expanded ? "w-full pl-2" : placement === "header" ? "size-10 justify-center" : "w-full justify-center"}`}
        data-control-id="profile.menu.trigger"
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        {profilePhotos[persona.id] ? (
          <img
            alt=""
            className="size-10 shrink-0 rounded-xl border border-sidebar-foreground/25 object-cover"
            src={profilePhotos[persona.id]}
          />
        ) : (
          <span className="grid size-10 shrink-0 place-items-center rounded-xl border border-sidebar-foreground/25 bg-sidebar-foreground/15 text-xs font-medium">
            {persona.initials}
          </span>
        )}
        {expanded ? (
          <span
            aria-hidden="true"
            className={`flex min-w-0 flex-1 items-center gap-2 pr-2 ${fadeClass(labelsVisible)}`}
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">{persona.name}</span>
              {roleTitle ? (
                <span className="block truncate text-xs text-sidebar-foreground/75">{roleTitle}</span>
              ) : null}
            </span>
            <ChevronsUpDown aria-hidden="true" className="size-4 shrink-0 text-sidebar-foreground/75" />
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          className={`absolute z-50 w-72 overflow-hidden rounded-2xl border border-border bg-popover text-popover-foreground shadow-lg ${
            placement === "header" ? "top-[calc(100%+0.5rem)] right-0" : "bottom-0 left-[calc(100%+1.5rem)]"
          }`}
          id={menuId}
          role="menu"
        >
          {panel === "main" ? (
            <>
              <div className="flex items-center gap-3 border-b border-border px-4 py-3.5">
                {profilePhotos[persona.id] ? (
                  <img
                    alt=""
                    className="size-11 shrink-0 rounded-xl object-cover"
                    src={profilePhotos[persona.id]}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="grid size-11 shrink-0 place-items-center rounded-xl bg-primary text-sm font-medium text-primary-foreground"
                  >
                    {persona.initials}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{persona.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{email}</p>
                </div>
              </div>
              <div className="border-b border-border px-3 py-3">
                <label className="text-xs font-medium text-muted-foreground" htmlFor={switcherId}>
                  {t("common.activePersona")}
                </label>
                <select
                  aria-label={t("common.activePersona")}
                  className="mt-1.5 min-h-10 w-full rounded-xl border border-input bg-background px-3 text-sm font-medium text-foreground shadow-xs focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                  data-control-id="shell.persona-switcher"
                  id={switcherId}
                  onChange={(event) => {
                    onPersonaChange(event.target.value);
                    setOpen(false);
                  }}
                  value={persona.id}
                >
                  {personas.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name} · {t(`common.${personaRoleKey(entry)}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="border-b border-border p-1.5">
                {persona.kind === "employee" ? (
                  <Link
                    className="flex min-h-10 items-center gap-3 rounded-xl px-3 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                    data-control-id="profile.menu.profile"
                    onClick={() => setOpen(false)}
                    role="menuitem"
                    to="/me/profile"
                  >
                    <UserRound aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.7} />
                    {t("profile.myProfile")}
                  </Link>
                ) : null}
                <button
                  className="flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                  data-control-id="profile.menu.language"
                  onClick={() => setPanel("language")}
                  role="menuitem"
                  type="button"
                >
                  <Languages aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.7} />
                  <span className="flex-1 text-left">{t("common.language")}</span>
                  <ChevronRight aria-hidden="true" className="size-4 text-muted-foreground" />
                </button>
              </div>
              <div className="p-1.5">
                <button
                  className="flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-destructive-foreground hover:bg-destructive focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25 disabled:cursor-not-allowed disabled:text-destructive-foreground disabled:opacity-100"
                  data-control-id="profile.menu.sign-out"
                  disabled={!canSignOut || logout.isPending}
                  onClick={() => logout.mutate()}
                  role="menuitem"
                  type="button"
                >
                  <LogOut aria-hidden="true" className="size-4 shrink-0" strokeWidth={1.7} />
                  {t("login.signOut")}
                </button>
                {logout.isError ? (
                  <p className="px-3 pb-2 text-xs font-medium text-destructive-foreground" role="alert">
                    {t("login.logoutFailed")}
                  </p>
                ) : null}
              </div>
            </>
          ) : (
            <div className="p-1.5">
              <button
                className="flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                data-control-id="profile.menu.language.back"
                onClick={() => setPanel("main")}
                role="menuitem"
                type="button"
              >
                <ChevronRight aria-hidden="true" className="size-4 shrink-0 rotate-180" />
                {t("common.language")}
              </button>
              {(
                [
                  { code: "nl", labelKey: "profile.languageNl" },
                  { code: "en", labelKey: "profile.languageEn" },
                ] as const
              ).map((option) => {
                const active = i18n.language === option.code;
                return (
                  <button
                    aria-checked={active}
                    className={`flex min-h-10 w-full items-center gap-3 rounded-xl px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25 ${active ? "bg-muted text-foreground" : "text-foreground hover:bg-muted"}`}
                    data-control-id={`profile.menu.language.${option.code}`}
                    key={option.code}
                    onClick={() => chooseLanguage(option.code)}
                    role="menuitemradio"
                    type="button"
                  >
                    {t(option.labelKey)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
